/**
 * '홈 화면에 추가' 를 부를 수 있는 순간을 붙잡아 둔다.
 *
 * 크롬은 페이지가 열리고 곧바로 beforeinstallprompt 를 던진다. 그런데 이걸
 * 듣던 곳이 화면 안쪽 컴포넌트여서, React 가 그려지기 전에 튄 이벤트를
 * 통째로 놓쳤다. 그래서 '설치' 버튼이 끝내 나오지 않았고 띠는 눌러도 아무
 * 일이 없었다.
 *
 * 그래서 듣는 자리를 index.js 맨 앞으로 옮기고, 받은 것을 여기 담아둔다.
 * 컴포넌트는 나중에 붙어도 꺼내 쓸 수 있다.
 *
 * 이 이벤트는 한 번 쓰면 다시 쓸 수 없다. prompt() 한 뒤에는 반드시 비운다.
 */
let saved = null;
const listeners = new Set();

const tell = () => listeners.forEach((fn) => fn(saved));

/** index.js 가 맨 앞에서 한 번 부른다. */
export function watchInstallPrompt() {
  if (typeof window === "undefined") return;

  window.addEventListener("beforeinstallprompt", (e) => {
    /* 크롬이 스스로 띄우는 안내를 막고, 우리가 원하는 때에 부른다. */
    e.preventDefault();
    saved = e;
    tell();
  });

  /* 설치가 끝나면 더 권할 이유가 없다. 홈 화면에 이미 있는 사람에게
     '홈 화면에 추가하세요' 가 계속 떠 있으면 고장으로 보인다. */
  window.addEventListener("appinstalled", () => {
    saved = null;
    tell();
  });
}

/** 지금 부를 수 있으면 그 이벤트를, 아니면 null. */
export function getInstallPrompt() {
  return saved;
}

/**
 * 설치를 부른다. 부를 수 없는 상태면 false 를 돌려준다 — 부르는 쪽이
 * 다른 길(아이폰 안내)로 갈 수 있게 하려는 것이다.
 */
export async function callInstallPrompt() {
  if (!saved) return false;
  const e = saved;
  /* 한 번 쓴 이벤트는 다시 못 쓴다. 결과를 기다리기 전에 비워서,
     연달아 누를 때 같은 것을 두 번 부르지 않게 한다. */
  saved = null;
  tell();
  try {
    e.prompt();
    await e.userChoice;
  } catch {
    /* 이미 쓴 이벤트였거나 사용자가 창을 닫은 경우. 띠는 어차피 접힌다. */
  }
  return true;
}

/** 바뀔 때마다 알려준다. 끊는 함수를 돌려준다. */
export function onInstallPromptChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** 아이폰·아이패드인가. 애플은 설치를 코드로 부르는 길을 열어두지 않았다. */
export function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  /* 아이패드는 최근 판에서 자기를 맥이라고 말한다. 손가락이 닿는지로 가른다. */
  const iPadOS = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  return (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) || iPadOS;
}

/** 이미 홈 화면에서 실행 중인가 */
export function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}
